import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * ChessDiagramGenerator
 * ---------------------
 * - Paste a FEN string (the side-to-move field is read from FEN field 2).
 * - Toggle "Show from Black's perspective".
 * - Pick a piece set: Unicode symbols, or any of the open-source SVG sets
 *   from lichess-org/lila (public/piece/<set>/{w|b}{K|Q|R|B|N|P}.svg).
 * - Choose a base filename.
 * - Export as HTML, PNG, or JPG.
 *
 * Visual layout (outer → inner):
 *   1. A uniform 17px BLACK border on all four sides — the coordinates
 *      a-h / 1-8 are rendered in WHITE inside this band, centered on each
 *      file/rank.
 *   2. A 3px WHITE border between the black frame and the squares.
 *   3. The 480×480 chessboard.
 *   4. A small disc OUTSIDE the right-hand black border indicates the side
 *      to move: ~15px clear of the border, vertically centered on the 2nd
 *      rank when White is to move and on the 7th rank when Black is.
 *      (When the board is flipped, the disc follows its rank — so it stays
 *      on the side of the player whose move it is.)
 *
 * Border thicknesses are specified at the default 480px board size and
 * scale proportionally when the SVG is rendered larger (e.g. for PNG/JPG
 * export at 960px).
 */

// const STARTING_FEN =
//   "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

const STARTING_FEN = "r1b1k2r/2qnbppp/p2ppn2/3N2B1/1p1NPP2/3B1Q2/PPP3PP/2KRR3 b kq - 3 11"

const PIECE_GLYPHS = {
  K: "♔", Q: "♕", R: "♖", B: "♗", N: "♘", P: "♙",
  k: "♚", q: "♛", r: "♜", b: "♝", n: "♞", p: "♟",
};

// const DEFAULT_LIGHT_SQUARE = "#f0d9b5";
// const DEFAULT_DARK_SQUARE  = "#b58863";

const DEFAULT_LIGHT_SQUARE = "#ffffdd";
const DEFAULT_DARK_SQUARE  = "#8ba56c";
const DEFAULT_BORDER_COLOR = "#000000";

const LICHESS_BOARD_THEMES = [
  { key: "canvas",        label: "Canvas",         light: "#d7daeb", dark: "#547388" },
  { key: "blue",          label: "Blue",          light: "#dee3e6", dark: "#788a94" },
  { key: "blue2",         label: "Blue 2",         light: "#97b2c7", dark: "#546f82" },
  { key: "blue3",         label: "Blue 3",         light: "#d9e0e6", dark: "#315991" },
  { key: "blue-marble",   label: "Blue Marble",    light: "#eae6dd", dark: "#7c7f87" },
  { key: "brown",         label: "Brown",          light: "#f0d9b5", dark: "#946f51" },
  { key: "cocoa",         label: "Cocoa",          light: "#d8d1c5", dark: "#a1806b" },
  { key: "green",         label: "Green",          light: "#ffffdd", dark: "#6d8753" },
  { key: "green-plastic", label: "Green Plastic",  light: "#f2f9bb", dark: "#59935d" },
  { key: "grey",          label: "Grey",           light: "#b8b8b8", dark: "#7d7d7d" },
  { key: "horsey",        label: "Horsey",         light: "#f0d9b5", dark: "#946f51" },
  { key: "ic",            label: "IC",             light: "#ececec", dark: "#c1c18e" },
  { key: "leather",       label: "Leather",        light: "#d1d1c9", dark: "#c28e16" },
  { key: "maple",         label: "Maple",          light: "#e8ceab", dark: "#bc7944" },
  { key: "maple2",        label: "Maple 2",        light: "#e2c89f", dark: "#996633" },
  { key: "marble",        label: "Marble",         light: "#93ab91", dark: "#4f644e" },
  { key: "metal",         label: "Metal",          light: "#c9c9c9", dark: "#727272" },
  { key: "newspaper",     label: "Newspaper",      light: "#ffffff", dark: "#8d8d8d" },
  { key: "olive",         label: "Olive",          light: "#b8b19f", dark: "#6d6655" },
  { key: "parchment",     label: "Parchment",      light: "#c0b19d", dark: "#946d46" },
  { key: "pink",          label: "Pink Pyramid",   light: "#e8e9b7", dark: "#ed7272" },
  { key: "purple",        label: "Purple",         light: "#9f90b0", dark: "#7d4a8d" },
  { key: "purple-diag",   label: "Purple Diag",    light: "#e5daf0", dark: "#957ab0" },
  { key: "putt-putt",     label: "Putt Putt",      light: "#d8d1c5", dark: "#6d8b44" },
  { key: "wood",          label: "Wood",           light: "#d8a45b", dark: "#9b4d0f" },
  { key: "wood2",         label: "Wood 2",         light: "#a38b5d", dark: "#6c5017" },
  { key: "wood3",         label: "Wood 3",         light: "#d0ceca", dark: "#755839" },
  { key: "wood4",         label: "Wood 4",         light: "#caaf7d", dark: "#7b5330" },
  { key: "wood-worn",     label: "Wood Worn",      light: "#bd938b", dark: "#7e4f3b" },
];

const PIECE_NAMES = ["wK","wQ","wR","wB","wN","wP","bK","bQ","bR","bB","bN","bP"];

const LICHESS_BASE =
  "https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece";

const PIECE_SET_OPTIONS = [
  { key: "unicode",     label: "Unicode Symbols (offline)", type: "unicode" },
  { key: "cburnett",    label: "cburnett (Wikipedia standard)", type: "lichess" },
  { key: "alpha",       label: "Alpha", type: "lichess" },
  { key: "california",  label: "California", type: "lichess" },
  { key: "caliente",    label: "Caliente", type: "lichess" },
  { key: "cardinal",    label: "Cardinal", type: "lichess" },
  { key: "celtic",      label: "Celtic", type: "lichess" },
  { key: "chess7",      label: "Chess7", type: "lichess" },
  { key: "chessnut",    label: "Chessnut", type: "lichess" },
  { key: "companion",   label: "Companion", type: "lichess" },
  { key: "cooke",       label: "Cooke", type: "lichess" },
  { key: "disguised",   label: "Disguised", type: "lichess" },
  { key: "dubrovny",    label: "Dubrovny", type: "lichess" },
  { key: "fantasy",     label: "Fantasy", type: "lichess" },
  { key: "fresca",      label: "Fresca", type: "lichess" },
  { key: "gioco",       label: "Gioco", type: "lichess" },
  { key: "governor",    label: "Governor", type: "lichess" },
  { key: "horsey",      label: "Horsey", type: "lichess" },
  { key: "icpieces",    label: "ICPieces", type: "lichess" },
  { key: "kosal",       label: "Kosal", type: "lichess" },
  { key: "leipzig",     label: "Leipzig", type: "lichess" },
  { key: "letter",      label: "Letter", type: "lichess" },
  { key: "maestro",     label: "Maestro", type: "lichess" },
  { key: "merida",      label: "Mérida", type: "lichess" },
  { key: "mpchess",     label: "MP Chess", type: "lichess" },
  { key: "pirouetti",   label: "Pirouetti", type: "lichess" },
  { key: "pixel",       label: "Pixel", type: "lichess" },
  { key: "reillycraig", label: "Reilly Craig", type: "lichess" },
  { key: "riohacha",    label: "Riohacha", type: "lichess" },
  { key: "shapes",      label: "Shapes", type: "lichess" },
  { key: "spatial",     label: "Spatial", type: "lichess" },
  { key: "staunty",     label: "Staunty", type: "lichess" },
  { key: "tatiana",     label: "Tatiana", type: "lichess" },
];

const PIECE_SETS_BY_KEY = Object.fromEntries(
  PIECE_SET_OPTIONS.map((s) => [s.key, s])
);

function fenToLichessName(piece) {
  const color = piece === piece.toUpperCase() ? "w" : "b";
  return color + piece.toUpperCase();
}

/* ---------- Encoding helpers ---------- */

function utf8ToBase64(str) {
  const bytes = new TextEncoder().encode(str);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function svgStringToDataUrl(svg) {
  return `data:image/svg+xml;base64,${utf8ToBase64(svg)}`;
}

function escapeXml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/* ---------- Lichess piece fetching ---------- */

// Try /pieces/<set>/<piece>.svg (local public/) first; fall back to CDN.
async function fetchOnePiece(setKey, name, signal) {
  const localUrl = `/pieces/${setKey}/${name}.svg`;
  try {
    const res = await fetch(localUrl, { signal });
    if (res.ok) return svgStringToDataUrl(await res.text());
  } catch (e) {
    if (e.name === "AbortError") throw e;
    // local fetch failed — try CDN below
  }
  // CDN fallback
  const cdnUrl = `${LICHESS_BASE}/${setKey}/${name}.svg`;
  const res = await fetch(cdnUrl, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${name}`);
  return svgStringToDataUrl(await res.text());
}

async function fetchPieceSet(setKey, signal) {
  const out = {};
  const failures = [];
  await Promise.all(
    PIECE_NAMES.map(async (name) => {
      try {
        out[name] = await fetchOnePiece(setKey, name, signal);
      } catch (e) {
        if (e.name === "AbortError") throw e;
        failures.push(name);
      }
    })
  );
  if (failures.length) {
    throw new Error(
      `Could not fetch ${failures.length} piece(s) for "${setKey}": ${failures.join(", ")}`
    );
  }
  return out;
}

/* ---------- FEN parsing ---------- */

function parseFen(fen) {
  if (typeof fen !== "string" || !fen.trim()) {
    throw new Error("FEN is empty");
  }
  const fields = fen.trim().split(/\s+/);
  const placement = fields[0];
  const activeColor = fields[1] === "b" ? "b" : "w";

  const rows = placement.split("/");
  if (rows.length !== 8) {
    throw new Error(`FEN must have 8 ranks (got ${rows.length})`);
  }
  const board = Array.from({ length: 8 }, () => Array(8).fill(null));
  for (let r = 0; r < 8; r++) {
    let c = 0;
    for (const ch of rows[r]) {
      if (/[1-8]/.test(ch)) {
        c += parseInt(ch, 10);
      } else if (PIECE_GLYPHS[ch]) {
        if (c >= 8) throw new Error(`Rank ${8 - r} overflows`);
        board[r][c] = ch;
        c += 1;
      } else {
        throw new Error(`Invalid FEN character: "${ch}"`);
      }
    }
    if (c !== 8) {
      throw new Error(`Rank ${8 - r} does not sum to 8 squares`);
    }
  }
  return { board, activeColor };
}

/* ---------- Board SVG ---------- */

function buildBoardSvg({
  board,
  activeColor,
  flipped,
  setKey,
  pieceData,
  boardSize = 480,
  lightSquare = DEFAULT_LIGHT_SQUARE,
  darkSquare  = DEFAULT_DARK_SQUARE,
  borderColor = DEFAULT_BORDER_COLOR,
  coordPosition = "border",
  showSideToMoveDisc = true,
}) {
  const setMeta = PIECE_SETS_BY_KEY[setKey] || PIECE_SETS_BY_KEY.unicode;

  // All thicknesses scale with boardSize so a 960px export looks the same
  // as a 480px preview, just sharper.
  const whiteBorder      = boardSize * (3 / 480);
  const blackBorder      = boardSize * (17 / 480);
  const discRadius       = boardSize * (9 / 480);
  const discGap          = boardSize * (15 / 480);  // gap from black border to disc
  const discRightMargin  = boardSize * (8 / 480);   // breathing room past the disc
  const discStrokeW      = Math.max(1, discRadius * 0.22);

  const square = boardSize / 8;
  const frameSize  = boardSize + 2 * (whiteBorder + blackBorder);
  const rightExtra = showSideToMoveDisc ? discGap + 2 * discRadius + discRightMargin : 0;
  const totalWidth = frameSize + rightExtra;
  const totalHeight = frameSize;

  const frameTop    = 0;
  const frameBottom = frameSize;
  const boardLeft   = blackBorder + whiteBorder;
  const boardTop    = blackBorder + whiteBorder;

  const ranks = flipped ? [1, 2, 3, 4, 5, 6, 7, 8] : [8, 7, 6, 5, 4, 3, 2, 1];
  const files = flipped
    ? ["h", "g", "f", "e", "d", "c", "b", "a"]
    : ["a", "b", "c", "d", "e", "f", "g", "h"];

  const parts = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}">`
  );

  // Outer border frame — covers ONLY the framed board area, leaving the
  // right-hand strip clear so the disc sits outside the border.
  parts.push(
    `<rect x="0" y="${frameTop}" width="${frameSize}" height="${frameSize}" fill="${escapeXml(borderColor)}"/>`
  );
  // Inner white border (3px on each side).
  parts.push(
    `<rect x="${blackBorder}" y="${frameTop + blackBorder}" width="${frameSize - 2 * blackBorder}" height="${frameSize - 2 * blackBorder}" fill="#ffffff"/>`
  );

  // Squares
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const light = (r + c) % 2 === 0;
      parts.push(
        `<rect x="${boardLeft + c * square}" y="${boardTop + r * square}" width="${square}" height="${square}" fill="${light ? escapeXml(lightSquare) : escapeXml(darkSquare)}"/>`
      );
    }
  }

  // Pieces
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const sourceR = flipped ? 7 - r : r;
      const sourceC = flipped ? 7 - c : c;
      const piece = board[sourceR][sourceC];
      if (!piece) continue;
      const px = boardLeft + c * square;
      const py = boardTop + r * square;

      if (setMeta.type === "lichess" && pieceData) {
        const dataUrl = pieceData[fenToLichessName(piece)];
        if (dataUrl) {
          parts.push(
            `<image x="${px}" y="${py}" width="${square}" height="${square}" href="${dataUrl}" preserveAspectRatio="xMidYMid meet"/>`
          );
        }
      } else {
        const isWhite = piece === piece.toUpperCase();
        const cx = px + square / 2;
        const cy = py + square / 2;
        const fontSize = square * 0.78;
        const fill = isWhite ? "#ffffff" : "#000000";
        const sw = isWhite ? 1.4 : 1.0;
        parts.push(
          `<text x="${cx}" y="${cy + fontSize * 0.02}" font-family="'DejaVu Sans','Segoe UI Symbol','Apple Symbols','Arial Unicode MS',sans-serif" font-size="${fontSize}" text-anchor="middle" dominant-baseline="central" fill="${fill}" stroke="#000000" stroke-width="${sw}" paint-order="stroke">${escapeXml(PIECE_GLYPHS[piece])}</text>`
        );
      }
    }
  }

  // Coordinates: either centered on the black frame (default) or tucked
  // inside the squares themselves (lichess-style).
  const coordFamily = "'DejaVu Sans','Segoe UI',Arial,sans-serif";

  if (coordPosition === "inside") {
    // Files (a–h) go in the lower-left corner of the bottom row of squares.
    // Ranks (1–8) go in the upper-right corner of the right-most column.
    // The label color is the OPPOSITE square color so it stays legible.
    const insideFontSize = Math.max(7, square * 0.18);
    const pad = square * 0.06;
    const lastRow = 7;
    const lastCol = 7;

    for (let c = 0; c < 8; c++) {
      const isLight = (lastRow + c) % 2 === 0;
      const fillColor = isLight ? darkSquare : lightSquare;
      const x = boardLeft + c * square + pad;
      const y = boardTop + (lastRow + 1) * square - pad;
      parts.push(
        `<text x="${x}" y="${y}" font-family=${JSON.stringify(coordFamily)} font-size="${insideFontSize}" font-weight="300" fill="${escapeXml(fillColor)}" text-anchor="start" dominant-baseline="text-after-edge">${files[c]}</text>`
      );
    }
    for (let r = 0; r < 8; r++) {
      const isLight = (r + lastCol) % 2 === 0;
      const fillColor = isLight ? darkSquare : lightSquare;
      const x = boardLeft + (lastCol + 1) * square - pad;
      const y = boardTop + r * square + pad;
      parts.push(
        `<text x="${x}" y="${y}" font-family=${JSON.stringify(coordFamily)} font-size="${insideFontSize}" font-weight="300" fill="${escapeXml(fillColor)}" text-anchor="end" dominant-baseline="hanging">${ranks[r]}</text>`
      );
    }
  } else {
    // Original "border" placement: white text on the black frame, centered
    // on each file/rank and on each of the four sides.
    const coordFontSize = Math.max(8, blackBorder * 0.72);
    const coordAttr =
      `font-family=${JSON.stringify(coordFamily)} font-size="${coordFontSize}" font-weight="300" fill="#ffffff" text-anchor="middle" dominant-baseline="central"`;

    for (let c = 0; c < 8; c++) {
      const x = boardLeft + (c + 0.5) * square;
      const yTop = frameTop + blackBorder / 2;
      const yBottom = frameBottom - blackBorder / 2;
      parts.push(`<text x="${x}" y="${yTop}" ${coordAttr}>${files[c]}</text>`);
      parts.push(`<text x="${x}" y="${yBottom}" ${coordAttr}>${files[c]}</text>`);
    }
    for (let r = 0; r < 8; r++) {
      const y = boardTop + (r + 0.5) * square;
      const xLeft = blackBorder / 2;
      const xRight = frameSize - blackBorder / 2;
      parts.push(`<text x="${xLeft}" y="${y}" ${coordAttr}>${ranks[r]}</text>`);
      parts.push(`<text x="${xRight}" y="${y}" ${coordAttr}>${ranks[r]}</text>`);
    }
  }

  // Side-to-move disc, ~15px right of the black border, centered on
  // rank 2 (White to move) or rank 7 (Black to move). The row index of
  // those ranks depends on whether the board is flipped.
  if (showSideToMoveDisc) {
    const targetRank = activeColor === "w" ? 1 : 8;
    const targetRow = flipped ? targetRank - 1 : 8 - targetRank;
    const dcx = frameSize + discGap + discRadius;
    const dcy = boardTop + (targetRow + 0.5) * square;
    const discFill = activeColor === "w" ? "#ffffff" : "#000000";
    const discStroke = activeColor === "w" ? "#000000" : "#ffffff";
    parts.push(
      `<circle cx="${dcx}" cy="${dcy}" r="${discRadius}" fill="${discFill}" stroke="${discStroke}" stroke-width="${discStrokeW}"/>`
    );
  }

  parts.push("</svg>");
  return parts.join("");
}

/* ---------- Download / export helpers ---------- */

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function svgToRasterDataUrl(
  svgString,
  { mime = "image/png", quality = 0.92 } = {}
) {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      // JPEG has no transparency; fill white background. (Harmless for PNG.)
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      try {
        resolve(
          mime === "image/jpeg"
            ? canvas.toDataURL("image/jpeg", quality)
            : canvas.toDataURL("image/png")
        );
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to rasterize SVG"));
    };
    img.src = url;
  });
}

function dataUrlToBlob(dataUrl) {
  const [header, base64] = dataUrl.split(",");
  const mimeMatch = header.match(/data:([^;]+)/);
  const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
  const bin = atob(base64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function buildStandaloneHtml({ svg, fen, flipped, setLabel, activeColor }) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Chess diagram</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
         margin: 2rem; color: #222; }
  .board svg { display: block; }
  .meta { margin-top: 1rem; font-size: 0.9rem; color: #555; }
  code { background: #f4f4f4; padding: 0.1rem 0.35rem; border-radius: 4px; }
</style>
</head>
<body>
  <div class="board">${svg}</div>
  <div class="meta">
    <div><strong>FEN:</strong> <code>${escapeXml(fen)}</code></div>
    <div><strong>Side to move:</strong> ${activeColor === "w" ? "White" : "Black"}</div>
    <div><strong>Orientation:</strong> ${flipped ? "Black to bottom" : "White to bottom"}</div>
    <div><strong>Piece set:</strong> ${escapeXml(setLabel)}</div>
  </div>
</body>
</html>
`;
}

function sanitizeBaseName(name) {
  const trimmed = (name || "").trim();
  if (!trimmed) return "chess-diagram";
  const noPath = trimmed.replace(/[\\/]/g, "_");
  // eslint-disable-next-line
  const noControl = noPath.replace(/[\x00-\x1f<>:"|?*]/g, "");
  const noExt = noControl.replace(/\.(html?|png|jpe?g|svg|pgn)$/i, "");
  return noExt || "chess-diagram";
}

/* ---------- Component ---------- */

// Named exports so other components can reuse the pure rendering helpers
// without duplicating logic.
export { buildBoardSvg, parseFen, fetchPieceSet, PIECE_SETS_BY_KEY,
         DEFAULT_LIGHT_SQUARE, DEFAULT_DARK_SQUARE, DEFAULT_BORDER_COLOR };

export const DEFAULT_SHOW_SIDE_TO_MOVE_DISC = false;

/**
 * @param {object} props
 * @param {object}   [props.initialPrefs]          Seed state from saved preferences.
 * @param {function} [props.onPreferencesChange]   Called with current prefs whenever they change.
 */
export default function ChessDiagramGenerator({ initialPrefs, onPreferencesChange, maxBoardSize = 480, boardSize: fixedBoardSize } = {}) {
  const ip = initialPrefs || {};
  const [fen, setFen] = useState(STARTING_FEN);
  const [flipped, setFlipped] = useState(ip.diagramFlipped ?? false);
  const [pieceSet, setPieceSet] = useState(ip.diagramPieceSet ?? "cburnett");
  const [filename, setFilename] = useState("chess-diagram");
  const [lichessTheme, setLichessTheme] = useState("");
  const [lightSquare, setLightSquare] = useState(ip.diagramLightSquare ?? DEFAULT_LIGHT_SQUARE);
  const [darkSquare,  setDarkSquare]  = useState(ip.diagramDarkSquare  ?? DEFAULT_DARK_SQUARE);
  const [borderColor, setBorderColor] = useState(ip.diagramBorderColor ?? DEFAULT_BORDER_COLOR);
  const [coordPosition, setCoordPosition] = useState(ip.diagramCoordPosition ?? "border");
  const [showSideToMoveDisc, setShowSideToMoveDisc] = useState(ip.diagramShowSideToMoveDisc ?? true);

  // Notify parent whenever any preference-related state changes
  const notifyRef = useRef(onPreferencesChange);
  notifyRef.current = onPreferencesChange;
  useEffect(() => {
    if (notifyRef.current) {
      notifyRef.current({
        diagramPieceSet:            pieceSet,
        diagramLightSquare:         lightSquare,
        diagramDarkSquare:          darkSquare,
        diagramBorderColor:         borderColor,
        diagramCoordPosition:       coordPosition,
        diagramFlipped:             flipped,
        diagramShowSideToMoveDisc:  showSideToMoveDisc,
      });
    }
  }, [pieceSet, lightSquare, darkSquare, borderColor, coordPosition, flipped, showSideToMoveDisc]);

  const cacheRef = useRef({});
  const [cacheVersion, setCacheVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // ── Responsive board sizing ───────────────────────────────────────────────
  // When fixedBoardSize is provided the board is always that exact size and the
  // ResizeObserver is skipped entirely.  Otherwise the board column width is
  // observed and boardSize is derived from it, capped at maxBoardSize.
  const boardAreaRef = useRef(null);
  const [boardAreaWidth, setBoardAreaWidth] = useState(561);

  useEffect(() => {
    if (fixedBoardSize) return; // fixed size — no observer needed
    const el = boardAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      setBoardAreaWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fixedBoardSize]);

  const previewBoardSize = fixedBoardSize
    ?? Math.max(240, Math.min(maxBoardSize, Math.floor(boardAreaWidth * (480 / 561))));

  // Total SVG width for the current board size (always includes disc space so
  // the layout stays stable when the "show disc" checkbox is toggled).
  const svgTotalWidth = (() => {
    const wb = previewBoardSize * (3  / 480);
    const bb = previewBoardSize * (17 / 480);
    const dr = previewBoardSize * (9  / 480);
    const dg = previewBoardSize * (15 / 480);
    const dm = previewBoardSize * (8  / 480);
    const fs = previewBoardSize + 2 * (wb + bb);
    const re = dg + 2 * dr + dm;          // always include disc space
    return Math.ceil(fs + re);
  })();

  const setMeta = PIECE_SETS_BY_KEY[pieceSet] || PIECE_SETS_BY_KEY.unicode;
  const pieceData =
    setMeta.type === "lichess" ? cacheRef.current[pieceSet] || null : null;

  useEffect(() => {
    if (setMeta.type !== "lichess") return;
    if (cacheRef.current[pieceSet]) return;

    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchPieceSet(pieceSet, controller.signal)
      .then((data) => {
        if (cancelled) return;
        cacheRef.current[pieceSet] = data;
        setCacheVersion((v) => v + 1);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled || e.name === "AbortError") return;
        setLoadError(e.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [pieceSet, setMeta.type]);

  const { board, activeColor, fenError } = useMemo(() => {
    try {
      const parsed = parseFen(fen);
      return { board: parsed.board, activeColor: parsed.activeColor, fenError: null };
    } catch (e) {
      return { board: null, activeColor: "w", fenError: e.message };
    }
  }, [fen]);

  const previewSvg = useMemo(() => {
    if (!board) return null;
    if (setMeta.type === "lichess" && !pieceData) return null;
    return buildBoardSvg({
      board,
      activeColor,
      flipped,
      setKey: pieceSet,
      pieceData,
      boardSize: previewBoardSize,
      lightSquare,
      darkSquare,
      borderColor,
      coordPosition,
      showSideToMoveDisc,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, activeColor, flipped, pieceSet, pieceData, cacheVersion, setMeta.type, lightSquare, darkSquare, borderColor, coordPosition, showSideToMoveDisc, previewBoardSize]);

  const baseName = sanitizeBaseName(filename);
  const setLabel = setMeta.label;

  const piecesReady = setMeta.type !== "lichess" || !!pieceData;
  const exportDisabled = !board || !piecesReady;

  const handleSaveHtml = () => {
    if (exportDisabled) return;
    const svg = buildBoardSvg({
      board, activeColor, flipped, setKey: pieceSet, pieceData, boardSize: 480,
      lightSquare, darkSquare, borderColor, coordPosition, showSideToMoveDisc,
    });
    const html = buildStandaloneHtml({ svg, fen, flipped, setLabel, activeColor });
    downloadBlob(`${baseName}.html`, new Blob([html], { type: "text/html" }));
  };

  const handleSavePng = async () => {
    if (exportDisabled) return;
    const svg = buildBoardSvg({
      board, activeColor, flipped, setKey: pieceSet, pieceData, boardSize: 960,
      lightSquare, darkSquare, borderColor, coordPosition, showSideToMoveDisc,
    });
    try {
      const dataUrl = await svgToRasterDataUrl(svg, { mime: "image/png" });
      downloadBlob(`${baseName}.png`, dataUrlToBlob(dataUrl));
    } catch (e) {
      alert(`Could not export PNG: ${e.message}`);
    }
  };

  const handleSaveJpg = async () => {
    if (exportDisabled) return;
    const svg = buildBoardSvg({
      board, activeColor, flipped, setKey: pieceSet, pieceData, boardSize: 960,
      lightSquare, darkSquare, borderColor, coordPosition, showSideToMoveDisc,
    });
    try {
      const dataUrl = await svgToRasterDataUrl(svg, { mime: "image/jpeg", quality: 0.92 });
      downloadBlob(`${baseName}.jpg`, dataUrlToBlob(dataUrl));
    } catch (e) {
      alert(`Could not export JPG: ${e.message}`);
    }
  };

  // minWidth ensures the wrapper is never narrower than the diagram column
  // plus a reasonable controls column.  328 = min controls width (280px)
  // + wrapper horizontal padding (2×16px) + column gap (16px).
  const minWrapperWidth = svgTotalWidth + 328;

  return (
    <div style={{ ...styles.wrapper, minWidth: minWrapperWidth }}>
      <div style={styles.controls}>
        <label style={styles.label} htmlFor="fen-input">FEN</label>
        <textarea
          id="fen-input"
          value={fen}
          onChange={(e) => setFen(e.target.value)}
          rows={1}
          spellCheck={false}
          style={{ ...styles.textarea, width: "100%", boxSizing: "border-box", minWidth: "calc(70ch + 1rem)" }}
          placeholder="Paste a FEN string..."
        />
        {board && (
          <div style={styles.statusLine}>
            <span style={styles.statusLabel}>To move:</span>
            <span
              style={{
                ...styles.miniDisc,
                background: activeColor === "w" ? "#ffffff" : "#000000",
                borderColor: activeColor === "w" ? "#000000" : "#666666",
              }}
              title={activeColor === "w" ? "White" : "Black"}
            />
            <span>{activeColor === "w" ? "White" : "Black"}</span>
            <label style={styles.inlineCheckbox}>
              <input
                type="checkbox"
                checked={showSideToMoveDisc}
                onChange={(e) => setShowSideToMoveDisc(e.target.checked)}
              />
              <span>Show side to move disc</span>
            </label>
          </div>
        )}

        <label style={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={flipped}
            onChange={(e) => setFlipped(e.target.checked)}
          />
          <span>Show from Black's perspective</span>
        </label>

        <label style={styles.label} htmlFor="set-select">Piece set</label>
        <select
          id="set-select"
          value={pieceSet}
          onChange={(e) => setPieceSet(e.target.value)}
          style={{ ...styles.select, width: "300px" }}
        >
          {PIECE_SET_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
        {setMeta.type === "lichess" && (
          <div style={styles.hint}>
            Source: lichess-org/lila · public/piece/{pieceSet}/ ·
            fetched once via jsDelivr and inlined into exports.
          </div>
        )}
        {loading && <div style={styles.info}>Loading piece set…</div>}
        {loadError && (
          <div style={styles.error}>
            Could not load "{setLabel}": {loadError}
          </div>
        )}

        <label style={styles.label}>Lichess theme</label>
        <select
          value={lichessTheme}
          onChange={(e) => {
            const key = e.target.value;
            setLichessTheme(key);
            if (key === "") return;
            const theme = LICHESS_BOARD_THEMES.find(t => t.key === key);
            if (theme) {
              setLightSquare(theme.light);
              setDarkSquare(theme.dark);
              setBorderColor(theme.dark);
            }
          }}
          style={{ ...styles.select, width: "300px" }}
        >
          <option value="">— select a theme —</option>
          {LICHESS_BOARD_THEMES.map(t => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>

        <label style={styles.label}>Board colors</label>
        <div style={styles.colorRow}>
          <label style={styles.colorItem}>
            <input
              type="color"
              value={lightSquare}
              onChange={(e) => setLightSquare(e.target.value)}
              style={styles.colorSwatch}
              title="Light square color"
            />
            <span style={styles.colorLabel}>Light squares</span>
          </label>
          <label style={styles.colorItem}>
            <input
              type="color"
              value={darkSquare}
              onChange={(e) => setDarkSquare(e.target.value)}
              style={styles.colorSwatch}
              title="Dark square color"
            />
            <span style={styles.colorLabel}>Dark squares</span>
          </label>
          <label style={styles.colorItem}>
            <input
              type="color"
              value={borderColor}
              onChange={(e) => setBorderColor(e.target.value)}
              style={styles.colorSwatch}
              title="Border color"
            />
            <span style={styles.colorLabel}>Border</span>
          </label>
          <button
            onClick={() => {
              setLichessTheme("");
              setLightSquare(DEFAULT_LIGHT_SQUARE);
              setDarkSquare(DEFAULT_DARK_SQUARE);
              setBorderColor(DEFAULT_BORDER_COLOR);
            }}
            style={{ ...styles.button, ...styles.secondary, fontSize: "0.78rem", padding: "0.3rem 0.6rem" }}
            title="Reset colors to defaults"
          >
            Reset colors
          </button>
        </div>

        <label style={styles.label}>Coordinates</label>
        <div style={styles.radioGroup}>
          <label style={styles.radioItem}>
            <input
              type="radio"
              name="coord-position"
              value="border"
              checked={coordPosition === "border"}
              onChange={() => setCoordPosition("border")}
            />
            <span>On the outside black border (all four sides)</span>
          </label>
          <label style={styles.radioItem}>
            <input
              type="radio"
              name="coord-position"
              value="inside"
              checked={coordPosition === "inside"}
              onChange={() => setCoordPosition("inside")}
            />
            <span>
              Inside squares — files in bottom-row lower-left, ranks in h-file upper-right
            </span>
          </label>
        </div>

        <label style={styles.label} htmlFor="filename-input">Filename</label>
        <div style={styles.filenameRow}>
          <input
            id="filename-input"
            type="text"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            placeholder="chess-diagram"
            style={styles.filename}
          />
          <span style={styles.extHint}>.html / .png / .jpg</span>
        </div>

        <div style={styles.buttonRow}>
          <button onClick={handleSaveHtml} disabled={exportDisabled} style={styles.button}>
            Save as HTML
          </button>
          <button onClick={handleSavePng} disabled={exportDisabled} style={styles.button}>
            Save as PNG
          </button>
          <button onClick={handleSaveJpg} disabled={exportDisabled} style={styles.button}>
            Save as JPG
          </button>
          <button
            onClick={() => {
              setFen(STARTING_FEN);
              setFlipped(false);
              setPieceSet("cburnett");
              setFilename("chess-diagram");
              setLightSquare(DEFAULT_LIGHT_SQUARE);
              setDarkSquare(DEFAULT_DARK_SQUARE);
              setBorderColor(DEFAULT_BORDER_COLOR);
              setCoordPosition("border");
              setShowSideToMoveDisc(true);
            }}
            style={{ ...styles.button, ...styles.secondary }}
          >
            Reset
          </button>
        </div>

        {fenError && <div style={styles.error}>FEN error: {fenError}</div>}
      </div>

      <div ref={boardAreaRef} style={{ ...styles.boardArea, minWidth: svgTotalWidth, maxWidth: svgTotalWidth }}>
        {previewSvg ? (
          <div
            style={styles.boardBox}
            dangerouslySetInnerHTML={{ __html: previewSvg }}
          />
        ) : (
          <div style={{ ...styles.placeholder, width: boardAreaWidth || 561 }}>
            {fenError
              ? "Fix the FEN to see the diagram."
              : loading
                ? "Loading piece set…"
                : loadError
                  ? "Pieces unavailable — pick another set or use Unicode."
                  : "Enter a valid FEN to see the diagram."}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "1rem",
    alignItems: "start",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: "#222",
    padding: "1rem",
  },
  controls: { display: "flex", flexDirection: "column", gap: "0.6rem" },
  label: { fontWeight: 600, fontSize: "0.9rem", marginTop: "0.2rem" },
  textarea: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    fontSize: "0.85rem",
    padding: "0.5rem",
    border: "1px solid #ccc",
    borderRadius: 6,
    resize: "vertical",
    minHeight: "3rem",
  },
  statusLine: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    fontSize: "0.85rem",
    color: "#444",
    marginTop: "-0.2rem",
  },
  statusLabel: { fontWeight: 600 },
  miniDisc: {
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: "1px solid",
  },
  select: {
    padding: "0.4rem 0.5rem",
    border: "1px solid #ccc",
    borderRadius: 6,
    fontSize: "0.9rem",
    background: "#fff",
  },
  hint: {
    fontSize: "0.75rem",
    color: "#666",
    fontStyle: "italic",
    marginTop: "-0.3rem",
  },
  info: {
    fontSize: "0.8rem",
    color: "#555",
    background: "#eef5ff",
    border: "1px solid #cfe0fa",
    padding: "0.4rem 0.6rem",
    borderRadius: 6,
  },
  colorRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.75rem",
  },
  colorItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
    cursor: "pointer",
    userSelect: "none",
  },
  colorSwatch: {
    width: 32,
    height: 32,
    padding: 0,
    border: "1px solid #ccc",
    borderRadius: 4,
    cursor: "pointer",
    background: "none",
  },
  colorLabel: { fontSize: "0.85rem" },
  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.3rem",
    fontSize: "0.85rem",
    color: "#333",
  },
  radioItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.45rem",
    cursor: "pointer",
    userSelect: "none",
    lineHeight: 1.3,
  },
  filenameRow: { display: "flex", alignItems: "center", gap: "0.5rem" },
  filename: {
    flex: 1,
    padding: "0.4rem 0.5rem",
    border: "1px solid #ccc",
    borderRadius: 6,
    fontSize: "0.9rem",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  },
  extHint: { fontSize: "0.8rem", color: "#888", whiteSpace: "nowrap" },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.95rem",
    cursor: "pointer",
    userSelect: "none",
  },
  inlineCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
    marginLeft: "0.75rem",
    fontSize: "0.85rem",
    cursor: "pointer",
    userSelect: "none",
  },
  buttonRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "0.3rem",
  },
  button: {
    padding: "0.5rem 0.9rem",
    border: "1px solid #b58863",
    background: "#f0d9b5",
    color: "#222",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: "0.9rem",
  },
  secondary: { background: "#fafafa", borderColor: "#ccc" },
  error: {
    background: "#fee",
    border: "1px solid #f99",
    color: "#900",
    padding: "0.5rem 0.75rem",
    borderRadius: 6,
    fontSize: "0.85rem",
  },
  boardArea: { display: "flex", justifyContent: "center" },
  boardBox: {
    padding: 0,
    lineHeight: 0,
  },
  placeholder: {
    height: 520,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f6f6f6",
    border: "1px dashed #bbb",
    color: "#888",
    fontSize: "0.95rem",
    textAlign: "center",
    padding: "1rem",
  },
};
